import { faUser } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import type { RenderField } from '@/components/blocks/collection/types';
import { resolveTextBlockContent, TextBlock } from '@/components/blocks/primitives/text-block';
import { CollectionCardFieldRenderer } from '@/components/blocks/renderers/collection-card-renderer';
import { StarRating } from '@/components/reviews-hub/ui/star-rating';
import { VerifiedBadge } from '@/components/reviews-hub/ui/verified-badge';
import { Icon } from '@/components/ui/icon';
import { Paragraph } from '@/components/ui/typography';
import type { CollectionCardConfig, CollectionItem } from '@/lib/blocks/collection/blocks';
import { getAlignmentClassName, getEnabledFields } from '@/lib/blocks/collection/blocks';
import { reviewDisplayText } from '@/lib/reviews/display-text';
import { richTextToPlainText } from '@/lib/rich-text';
import { cn } from '@/lib/utils';

export function buildReviewCollectionRenderField({
  item,
  cardConfig,
}: {
  item: CollectionItem;
  cardConfig: CollectionCardConfig;
}): RenderField {
  const align = cardConfig.align;
  const alignClassName = getAlignmentClassName(align);
  const enabledFields = getEnabledFields(cardConfig.fields);
  const showReviewerIcon = enabledFields.some((field) => field.slot === 'reviewerIcon');
  const showReviewerName = enabledFields.some((field) => field.slot === 'reviewerName');
  const reviewerSlots = new Set(['reviewerIcon', 'reviewerName']);
  const firstReviewerSlot =
    enabledFields.find((field) => reviewerSlots.has(field.slot))?.slot ?? null;

  return (field, value) => {
    if (field.slot === 'description') {
      const content = resolveTextBlockContent(
        reviewDisplayText(item as { reviewSummary?: unknown; description?: unknown }),
      );
      if (!content) return null;
      return (
        <div className="text-[14px] font-bold leading-5 [&_.prose]:max-w-none [&_.prose_p]:m-0">
          <TextBlock content={richTextToPlainText(content)} />
        </div>
      );
    }

    if (field.slot === 'rating') {
      if (item.showStarRatingOnWebsite === false) return null;
      const rating = typeof item.rating === 'number' ? item.rating : null;
      if (!rating) return null;
      return (
        <div className={cn('flex items-center', alignClassName)}>
          <StarRating rating={rating} className="text-primary" />
        </div>
      );
    }

    if (reviewerSlots.has(field.slot)) {
      if (field.slot !== firstReviewerSlot) return null;

      const name = richTextToPlainText(resolveTextBlockContent(item.reviewerName)) || 'Anonymous';
      return (
        <div className={cn('flex items-center gap-2.5', alignClassName)}>
          {showReviewerIcon ? (
            <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Icon icon={faUser} className="size-2.5" />
            </div>
          ) : null}
          {showReviewerName ? (
            <div className="flex items-center gap-1">
              <Paragraph className="m-0 text-[12px] leading-none font-bold">{name}</Paragraph>
              {cardConfig.sourceMode === 'dynamic' && item.verified === true ? (
                <VerifiedBadge />
              ) : null}
            </div>
          ) : null}
        </div>
      );
    }

    return <CollectionCardFieldRenderer field={field} value={value} align={align} />;
  };
}
